import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChartLineUp, Eye, EyeSlash } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-border relative overflow-hidden">
        <div className="flex items-center gap-3">
          <ChartLineUp size={28} weight="bold" className="text-primary" />
          <span className="font-display font-black text-xl tracking-tighter">Dashboard PowerBI</span>
        </div>
        <div>
          <h1 className="font-display font-black text-5xl tracking-tighter leading-[0.95]">
            Ubah Data Excel jadi <span className="text-primary">dashboard</span> bergaya Power BI
          </h1>
          <p className="mt-6 text-muted-foreground max-w-md text-base">
            Integrasikan excel atau spreadsheet hasilkan grafik interaktif dan analisa otomatis bertenaga AI
          </p>
        </div>
        <div className="hidden lg:block text-xs text-muted-foreground uppercase tracking-[0.16em] font-semibold">
          analytics engine v1.0
        </div>
        <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <ChartLineUp size={26} weight="bold" className="text-primary" />
            <span className="font-display font-black text-lg tracking-tighter">Dashboard Power BI</span>
          </div>
          <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-semibold">Selamat datang kembali</p>
          <h2 className="font-display font-bold text-3xl tracking-tight mt-2 mb-8">Masuk ke akun</h2>

          {error && (
            <div className="mb-4 text-sm text-destructive border border-destructive/40 bg-destructive/10 px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="anda@perusahaan.com" className="mt-1.5" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-muted-foreground">Kata Sandi</Label>
              <div className="relative mt-1.5">
                <Input id="password" type={show ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password-input" />
                <button type="button" onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="toggle-password">
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-xl font-semibold" data-testid="login-submit-button">
              {loading ? "Memproses..." : "Masuk"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            Belum punya akun?{" "}
            <Link to="/register" className="text-primary hover:underline font-medium" data-testid="go-register-link">Daftar</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
